import React, { useState, useEffect } from "react";
import { Sparkles } from "lucide-react";

const JUNIOR_ROTATING_MESSAGES = [
  "Junior Blocks is loading...",
  "Setting up colorful 3D blocks...",
  "Preparing your stage, scenes & characters...",
  "Getting Junior Blocks ready...",
  "Almost ready to play! ✨",
];

const JuniorShimmerLoader = ({
  subMode = "basic",
  isLoaded = false,
}) => {
  const [msgIndex, setMsgIndex] = useState(0);
  const [fadeMsg, setFadeMsg] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeMsg(false);
      setTimeout(() => {
        setMsgIndex((prev) => (prev + 1) % JUNIOR_ROTATING_MESSAGES.length);
        setFadeMsg(true);
      }, 200);
    }, 2200);

    return () => clearInterval(interval);
  }, []);

  const categoryColors =
    subMode === "advanced"
      ? [
          "bg-[#FDE047] border-b-[#CA8A04]", // Events
          "bg-[#60A5FA] border-b-[#2563EB]", // Motion
          "bg-[#C084FC] border-b-[#9333EA]", // Looks
          "bg-[#4ADE80] border-b-[#16A34A]", // Sound
          "bg-[#FB923C] border-b-[#EA580C]", // Control
          "bg-[#A3E635] border-b-[#65A30D]", // Operators
          "bg-[#F97316] border-b-[#C2410C]", // Variables
        ]
      : [
          "bg-[#FDE047] border-b-[#CA8A04]", // Events
          "bg-[#60A5FA] border-b-[#2563EB]", // Motion
          "bg-[#C084FC] border-b-[#9333EA]", // Looks
          "bg-[#4ADE80] border-b-[#16A34A]", // Sound
          "bg-[#FB923C] border-b-[#EA580C]", // Control
        ];

  return (
    <div
      className={`absolute inset-0 z-40 bg-[#FAF6EE] text-[#4A3B2C] flex flex-col transition-opacity duration-500 font-sans overflow-hidden select-none ${
        isLoaded ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
    >
      {/* 1. Junior Top Sticker Toolbar Skeleton (#junior-top-bar layout) */}
      <div className="h-[52px] bg-[#FAF6EE] border-b border-[#E2D6C2] px-4 flex items-center justify-center gap-2.5 flex-shrink-0 relative overflow-hidden">
        {/* Warm Shimmer Sweep */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200/45 to-transparent animate-shimmer pointer-events-none" />

        {/* Green Flag & Red Stop */}
        <div className="w-9 h-9 rounded-[11px] bg-[#DCFCE7] border border-[#86EFAC] border-b-[3px] border-b-[#4ADE80] animate-pulse" />
        <div className="w-9 h-9 rounded-[11px] bg-[#FEE2E2] border border-[#FCA5A5] border-b-[3px] border-b-[#F87171] animate-pulse" />

        {/* Vertical Divider */}
        <div className="h-7 w-[1.5px] bg-[#E2D6C2] mx-1" />

        {/* 6 Squarish-Circle Sticker Controls (ABC, Grid, Reset, Fullscreen, Backdrop, Numpad) */}
        <div className="w-10 h-10 rounded-[13px] bg-[#FEF3C7] border border-[#FDE68A] border-b-[3px] border-b-[#F59E0B] animate-pulse" />
        <div className="w-10 h-10 rounded-[13px] bg-[#EDE9FE] border border-[#DDD6FE] border-b-[3px] border-b-[#8B5CF6] animate-pulse" />
        <div className="w-10 h-10 rounded-[13px] bg-[#DBEAFE] border border-[#BFDBFE] border-b-[3px] border-b-[#3B82F6] animate-pulse" />
        <div className="w-10 h-10 rounded-[13px] bg-[#E0F2FE] border border-[#BAE6FD] border-b-[3px] border-b-[#0284C7] animate-pulse" />
        <div className="w-10 h-10 rounded-[13px] bg-[#FFEDD5] border border-[#FED7AA] border-b-[3px] border-b-[#EA580C] animate-pulse" />
        <div className="w-10 h-10 rounded-[13px] bg-[#CCFBF1] border border-[#99F6E4] border-b-[3px] border-b-[#0D9488] animate-pulse" />
      </div>

      {/* 2. Junior Studio Main Body */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Continuous Warm Shimmer Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200/30 to-transparent animate-shimmer pointer-events-none z-10" />

        {/* Left 60px 3D Category Keycap Rail */}
        <div className="w-[60px] bg-[#F3ECE0] border-r border-[#E2D6C2] py-3 flex flex-col items-center gap-3 flex-shrink-0">
          {categoryColors.map((cls, i) => (
            <div
              key={i}
              className={`w-11 h-11 rounded-[14px] ${cls} border border-white/70 border-b-[3.5px] shadow-2xs animate-pulse`}
            />
          ))}
        </div>

        {/* Junior Block Flyout Drawer */}
        <div className="w-[240px] sm:w-[290px] bg-[#FBF8F1] border-r border-[#E2D6C2] p-3.5 hidden sm:flex flex-col gap-3.5 flex-shrink-0">
          {[
            "w-[78%] bg-[#FEF08A]/70 border-[#FACC15] border-b-[#EAB308]",
            "w-[85%] bg-[#DBEAFE]/80 border-[#93C5FD] border-b-[#3B82F6]",
            "w-[72%] bg-[#DBEAFE]/80 border-[#93C5FD] border-b-[#3B82F6]",
            "w-[80%] bg-[#DBEAFE]/80 border-[#93C5FD] border-b-[#3B82F6]",
            "w-[68%] bg-[#F3E8FF]/80 border-[#D8B4FE] border-b-[#9333EA]",
            "w-[76%] bg-[#FFEDD5]/80 border-[#FDBA74] border-b-[#EA580C]",
          ].map((blockCls, idx) => (
            <div
              key={idx}
              className={`h-12 rounded-2xl border border-b-[4px] px-3 flex items-center justify-between animate-pulse shadow-2xs ${blockCls}`}
            >
              <div className="w-7 h-7 rounded-lg bg-white/80" />
              <div className="w-9 h-6 rounded-full bg-white/90 border border-black/10" />
            </div>
          ))}
        </div>

        {/* Center Junior Workspace Canvas */}
        <div
          className="flex-1 bg-[#FAF6EE] p-6 relative flex flex-col justify-between overflow-hidden"
          style={{
            backgroundImage: "radial-gradient(#DFD3C0 1.3px, transparent 1.3px)",
            backgroundSize: "24px 24px",
          }}
        >
          {/* Chunky 3D Junior Block Stack Skeleton on Canvas */}
          <div className="space-y-2.5 z-0 mt-2 ml-2">
            {/* Hat Event Block */}
            <div className="w-40 h-13 bg-[#FEF08A]/85 border border-[#FACC15] border-b-[4px] border-b-[#EAB308] rounded-t-[24px] rounded-b-2xl px-3.5 flex items-center gap-3 animate-pulse shadow-2xs">
              <div className="w-7 h-7 rounded-lg bg-white/85" />
              <div className="w-12 h-3 rounded-full bg-amber-600/20" />
            </div>
            {/* Motion Block */}
            <div className="w-52 h-12 bg-[#DBEAFE]/85 border border-[#93C5FD] border-b-[4px] border-b-[#3B82F6] rounded-2xl ml-3 px-3.5 flex items-center justify-between animate-pulse shadow-2xs">
              <div className="w-7 h-7 rounded-lg bg-white/85" />
              <div className="w-10 h-6 rounded-full bg-white/90 border border-blue-300/60" />
            </div>
            {/* Looks Block */}
            <div className="w-48 h-12 bg-[#F3E8FF]/85 border border-[#D8B4FE] border-b-[4px] border-b-[#9333EA] rounded-2xl ml-3 px-3.5 flex items-center justify-between animate-pulse shadow-2xs">
              <div className="w-7 h-7 rounded-lg bg-white/85" />
              <div className="w-12 h-6 rounded-full bg-white/90 border border-purple-300/60" />
            </div>
            {/* Control Block */}
            <div className="w-44 h-12 bg-[#FFEDD5]/85 border border-[#FDBA74] border-b-[4px] border-b-[#EA580C] rounded-2xl ml-3 px-3.5 flex items-center justify-between animate-pulse shadow-2xs">
              <div className="w-7 h-7 rounded-lg bg-white/85" />
              <div className="w-9 h-6 rounded-full bg-white/90 border border-orange-300/60" />
            </div>
          </div>

          {/* Floating Warm Junior Loading Status Pill */}
          <div className="relative z-20 self-center mb-5">
            <div className="flex items-center gap-3 bg-[#FFFDF9] border-2 border-[#E2C07D] rounded-full px-6 py-3 shadow-[0_5px_0_#D4AD60,0_12px_24px_rgba(45,35,20,0.14)] ring-4 ring-amber-100/80">
              <div className="w-3 h-3 rounded-full bg-[#EA580C] animate-ping flex-shrink-0" />
              <p
                className={`text-xs sm:text-sm font-extrabold text-[#5C3A21] tracking-wide transition-all duration-300 ${
                  fadeMsg ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"
                }`}
              >
                {JUNIOR_ROTATING_MESSAGES[msgIndex]}
              </p>
              <Sparkles className="w-4 h-4 text-[#F59E0B] animate-spin flex-shrink-0 ml-1" />
            </div>
          </div>
        </div>

        {/* Right Column: Stage + Resize Bar + Scenes Panel + Characters Panel */}
        <div className="w-[300px] lg:w-[360px] bg-[#F3ECE0]/75 border-l border-[#E2D6C2] p-3 hidden md:flex flex-col gap-2.5 flex-shrink-0">
          {/* 1. 4:3 Stage Preview Skeleton */}
          <div className="w-full h-48 bg-white rounded-[18px] border-2 border-[#E2D6C2] shadow-2xs relative overflow-hidden flex flex-col items-center justify-center p-3">
            <div className="w-14 h-14 bg-[#FEF3C7] border-2 border-[#F59E0B]/50 border-b-[4px] border-b-[#D97706]/60 rounded-2xl animate-bounce" />
            <div className="h-3 w-20 bg-[#E6DAC6]/80 rounded-full mt-3 animate-pulse" />
          </div>

          {/* 2. Junior Size / Sprite Info Bar Skeleton */}
          <div className="h-10 bg-[#FFFDF9] rounded-full border-2 border-[#E2D6C2] px-4 flex items-center justify-between shadow-2xs">
            <div className="h-4 w-14 bg-[#EDE4D3] rounded-full animate-pulse" />
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#FEE2E2] border border-[#FCA5A5] animate-pulse" />
              <div className="w-7 h-7 rounded-full bg-[#DCFCE7] border border-[#86EFAC] animate-pulse" />
              <div className="w-7 h-7 rounded-full bg-[#DBEAFE] border border-[#93C5FD] animate-pulse" />
            </div>
          </div>

          {/* 3. Scenes Panel Skeleton */}
          <div className="bg-[#FFFDF9] rounded-[16px] p-2.5 flex flex-col gap-2 border-2 border-[#E2D6C2] shadow-2xs">
            <div className="h-3 w-16 bg-[#D8CBB8] rounded-full animate-pulse" />
            <div className="flex items-center gap-2 pt-0.5">
              <div className="w-24 h-16 bg-[#F5EFE4] rounded-xl border-2 border-[#F59E0B]/60 animate-pulse flex-shrink-0" />
              <div className="w-20 h-16 bg-[#FAF6EE] rounded-xl border-2 border-dashed border-[#D5C3A1] animate-pulse flex-shrink-0" />
            </div>
          </div>

          {/* 4. Characters Panel Skeleton */}
          <div className="flex-1 bg-[#FFFDF9] rounded-[16px] p-2.5 flex flex-col gap-2 border-2 border-[#E2D6C2] shadow-2xs">
            <div className="h-3 w-24 bg-[#D8CBB8] rounded-full animate-pulse" />
            <div className="flex items-center gap-2.5 pt-0.5">
              <div className="w-20 h-22 bg-[#F5EFE4] rounded-xl border-2 border-[#10B981]/60 animate-pulse flex-shrink-0" />
              <div className="w-20 h-22 bg-[#FAF6EE] rounded-xl border-2 border-dashed border-[#D5C3A1] animate-pulse flex-shrink-0" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JuniorShimmerLoader;
